/**
 * Inbox de WhatsApp (`/whatsapp`): consultes i mutacions sobre `/whatsapp-inbox`
 * (`plans/whatsapp-inbox.md` §4, contracte A1 ↔ A2 del runbook 3 §3.0).
 *
 * Polling (§2bis e): 15 s a la llista i als recomptes, 5 s al fil obert. TanStack Query no
 * refresca en segon pla per defecte (`refetchIntervalInBackground: false`): no cal codi de
 * visibilitat.
 */

import {
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  WaContactSummary,
  WaConversationDetail,
  WaConversationListQuery,
  WaConversationPage,
  WaInboxCounts,
  WaLinkReservationInput,
  WaLinkReservationResult,
  WaMessagePage,
  WaInboxConfigPatch,
  WaNoteItem,
  WaObserveResult,
  WaPatchContact,
  WaPatchConversation,
  WaPmsSendResult,
  WaRenderTemplateInput,
  WaRenderTemplateResult,
  WaReservationBadge,
  MessageTemplate,
  WhatsappInboxConfig,
} from '@guilleumes/types';
import { api } from '@/services/api';

// ═══════════════════════════════════════════════════════════════
// Claus
// ═══════════════════════════════════════════════════════════════

/** Filtres de la llista: la consulta sense el cursor (el porta `useInfiniteQuery`). */
export type WaConversationFilters = Omit<WaConversationListQuery, 'cursor'>;

export const whatsappInboxKeys = {
  all: ['whatsapp-inbox'] as const,
  conversations: (filters: WaConversationFilters) =>
    [...whatsappInboxKeys.all, 'conversations', filters] as const,
  conversation: (id: string) => [...whatsappInboxKeys.all, 'conversation', id] as const,
  messages: (id: string) => [...whatsappInboxKeys.all, 'messages', id] as const,
  notes: (id: string) => [...whatsappInboxKeys.all, 'notes', id] as const,
  reservations: (id: string) => [...whatsappInboxKeys.all, 'reservations', id] as const,
  counts: (includePersonal = false) =>
    [...whatsappInboxKeys.all, 'counts', { includePersonal }] as const,
  templates: (lang: string) => [...whatsappInboxKeys.all, 'templates', lang] as const,
  config: () => [...whatsappInboxKeys.all, 'config'] as const,
  /** `mutationKey` de les accions pròpies sobre un fil (el fil hi baixa en acabar, D-b/D5). */
  threadAction: () => [...whatsappInboxKeys.all, 'thread-action'] as const,
};

const BASE = '/whatsapp-inbox';
export const WA_CONVERSATIONS_PAGE_SIZE = 50;
export const WA_MESSAGES_PAGE_SIZE = 50;
const LIST_REFETCH_MS = 15_000;
const THREAD_REFETCH_MS = 5_000;
const SIDEBAR_REFETCH_MS = 60_000;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

// ═══════════════════════════════════════════════════════════════
// Consultes
// ═══════════════════════════════════════════════════════════════

/** Llista paginada per cursor (50). Amb `text=true` el backend en torna 50 i cap cursor. */
export function useWhatsappConversations(filters: WaConversationFilters) {
  return useInfiniteQuery({
    queryKey: whatsappInboxKeys.conversations(filters),
    queryFn: async ({ pageParam }) => {
      try {
        const res = await api.get<WaConversationPage>(`${BASE}/conversations`, {
          params: { take: WA_CONVERSATIONS_PAGE_SIZE, ...filters, cursor: pageParam },
        });
        return res.data;
      } catch (error) {
        // `id` fix: el polling no apila un toast cada 15 s si el backend no respon.
        toast.error(errorMessage(error, "No s'han pogut carregar les converses"), {
          id: 'whatsapp-inbox-list',
        });
        throw error;
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchInterval: LIST_REFETCH_MS,
  });
}

/** Recomptes per pestanya. */
export function useWhatsappInboxCounts(includePersonal = false) {
  return useQuery({
    queryKey: whatsappInboxKeys.counts(includePersonal),
    queryFn: async () => {
      const res = await api.get<WaInboxCounts>(`${BASE}/conversations/counts`, {
        params: includePersonal ? { includePersonal: true } : undefined,
      });
      return res.data;
    },
    refetchInterval: LIST_REFETCH_MS,
  });
}

/** Badge de la barra lateral: converses obertes (sense les personals). */
export function useWhatsappInboxOpenCount(): number {
  const { data } = useQuery({
    queryKey: whatsappInboxKeys.counts(false),
    queryFn: async () => {
      const res = await api.get<WaInboxCounts>(`${BASE}/conversations/counts`);
      return res.data;
    },
    refetchInterval: SIDEBAR_REFETCH_MS,
    retry: false,
  });
  return data?.OPEN ?? 0;
}

export function useWhatsappConversation(id: string | undefined) {
  return useQuery({
    queryKey: whatsappInboxKeys.conversation(id ?? ''),
    queryFn: async () => {
      try {
        const res = await api.get<WaConversationDetail>(`${BASE}/conversations/${id}`);
        return res.data;
      } catch (error) {
        toast.error(errorMessage(error, "No s'ha pogut carregar la conversa"), {
          id: 'whatsapp-inbox-thread',
        });
        throw error;
      }
    },
    enabled: !!id,
    refetchInterval: THREAD_REFETCH_MS,
  });
}

/** Missatges del fil, més nou primer (com `/messaging/logs`); pàgines de 50 cap enrere. */
export function useWhatsappMessages(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: whatsappInboxKeys.messages(id ?? ''),
    queryFn: async ({ pageParam }) => {
      const res = await api.get<WaMessagePage>(`${BASE}/conversations/${id}/messages`, {
        params: { take: WA_MESSAGES_PAGE_SIZE, cursor: pageParam },
      });
      return res.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!id,
    refetchInterval: THREAD_REFETCH_MS,
  });
}

/** Notes, esborranys i activitats (`createdAt asc`). */
export function useWhatsappNotes(id: string | undefined) {
  return useQuery({
    queryKey: whatsappInboxKeys.notes(id ?? ''),
    queryFn: async () => {
      const res = await api.get<WaNoteItem[]>(`${BASE}/conversations/${id}/notes`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: THREAD_REFETCH_MS,
  });
}

/** Reserves que toquen els missatges de la conversa (la suggerida primer). */
export function useWhatsappConversationReservations(id: string | undefined) {
  return useQuery({
    queryKey: whatsappInboxKeys.reservations(id ?? ''),
    queryFn: async () => {
      const res = await api.get<WaReservationBadge[]>(`${BASE}/conversations/${id}/reservations`);
      return res.data;
    },
    enabled: !!id,
  });
}

/** Converses anteriors del contacte (panell): cerca pel telèfon a totes les pestanyes. */
export function useWhatsappContactConversations(phone: string | null | undefined) {
  return useQuery({
    queryKey: [...whatsappInboxKeys.all, 'conversations', { q: phone, tab: 'ALL', take: 10 }],
    queryFn: async () => {
      const res = await api.get<WaConversationPage>(`${BASE}/conversations`, {
        params: { q: phone, tab: 'ALL', take: 10, includePersonal: true },
      });
      return res.data.items;
    },
    enabled: !!phone,
  });
}

/**
 * El que el panell llegeix de `GET /reservations/:id` (forma de `ReservationResponseDto`).
 * `hasWhatsapp` i `doorCode` hi són opcionals: avui l'endpoint no els retorna.
 */
export interface WaPanelReservation {
  id: string;
  beds24_id?: string;
  data_entrada: string;
  data_sortida: string;
  nits: number;
  num_adults: number;
  num_nens: number;
  total_hostes?: number | null;
  estat: string;
  canal?: string | null;
  hora_arribada?: string | null;
  balance?: number | null;
  room?: { id: string; name: string } | null;
  unit?: { id: string; name: string; displayName?: string | null } | null;
  guests?: { id: string }[];
  guest_name?: string | null;
  guest_phone?: string | null;
  metadata?: { estatPagament?: string | null } | null;
  hasWhatsapp?: boolean | null;
  doorCode?: string | null;
}

/** Reserva seleccionada al panell (`GET /reservations/:id`, que ja existeix). */
export function useWhatsappPanelReservation(reservationId: string | null | undefined) {
  return useQuery({
    queryKey: [...whatsappInboxKeys.all, 'reservation', reservationId ?? ''],
    queryFn: async () => {
      const res = await api.get<WaPanelReservation>(`/reservations/${reservationId}`);
      return res.data;
    },
    enabled: !!reservationId,
    staleTime: 60_000,
  });
}

/**
 * Respostes ràpides del compositor (§4.3): plantilles actives en l'idioma de la conversa.
 * Clau pròpia: la de `useMessageTemplates` no porta `kind` ni idioma (`useMessagingTask.ts`).
 */
export function useInboxTemplates(lang: string, enabled = true) {
  return useQuery({
    queryKey: whatsappInboxKeys.templates(lang),
    queryFn: async () => {
      const res = await api.get<MessageTemplate[]>('/messaging/templates');
      return res.data.filter((t) => t.active && t.language === lang);
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

// ═══════════════════════════════════════════════════════════════
// Mutacions
// ═══════════════════════════════════════════════════════════════

function useInvalidateInbox() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: whatsappInboxKeys.all });
}

/**
 * D-b/D5: després d'una acció pròpia, el fil (detall, notes, missatges i reserves) es refà i
 * s'espera; la llista i els recomptes es refan sense esperar. Invalidar tot `whatsapp-inbox` i
 * esperar-ho feia que la línia nova trigués uns segons (totes les llistes paginades al darrere).
 */
function useInvalidateThread() {
  const queryClient = useQueryClient();
  return async (id: string): Promise<void> => {
    void queryClient.invalidateQueries({
      queryKey: whatsappInboxKeys.all,
      predicate: (query) => {
        const scope = query.queryKey[1];
        return scope === 'conversations' || scope === 'counts';
      },
    });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: whatsappInboxKeys.conversation(id) }),
      queryClient.invalidateQueries({ queryKey: whatsappInboxKeys.notes(id) }),
      queryClient.invalidateQueries({ queryKey: whatsappInboxKeys.messages(id) }),
      queryClient.invalidateQueries({ queryKey: whatsappInboxKeys.reservations(id) }),
    ]);
  };
}

/** Conversa d'una acció del fil: les variables són l'id o un objecte amb `id`. */
export function actionConversationId(variables: unknown): string | null {
  if (typeof variables === 'string') return variables;
  if (variables && typeof variables === 'object' && 'id' in variables) {
    const { id } = variables;
    return typeof id === 'string' ? id : null;
  }
  return null;
}

/**
 * Accions pròpies en curs sobre aquest fil. Compten fins que el fil s'ha refet (l'`onSuccess`
 * espera la invalidació): quan torna a 0, el fil ja té la línia nova.
 */
export function useThreadActionPending(conversationId: string): number {
  return useIsMutating({
    mutationKey: whatsappInboxKeys.threadAction(),
    predicate: (mutation) => actionConversationId(mutation.state.variables) === conversationId,
  });
}

export function usePatchConversation() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateThread();
  return useMutation<WaConversationDetail, Error, { id: string; patch: WaPatchConversation }>({
    mutationKey: whatsappInboxKeys.threadAction(),
    mutationFn: async ({ id, patch }) => {
      const res = await api.patch<WaConversationDetail>(`${BASE}/conversations/${id}`, patch);
      return res.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(whatsappInboxKeys.conversation(id), data);
      return invalidate(id);
    },
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut actualitzar la conversa")),
  });
}

export function useMarkSeen() {
  const invalidate = useInvalidateInbox();
  return useMutation<WaConversationDetail, Error, string>({
    mutationFn: async (id) => {
      const res = await api.post<WaConversationDetail>(`${BASE}/conversations/${id}/seen`);
      return res.data;
    },
    onSuccess: () => invalidate(),
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut marcar com a llegida")),
  });
}

export function useMarkUnseen() {
  const invalidate = useInvalidateThread();
  return useMutation<WaConversationDetail, Error, string>({
    mutationKey: whatsappInboxKeys.threadAction(),
    mutationFn: async (id) => {
      const res = await api.post<WaConversationDetail>(`${BASE}/conversations/${id}/unseen`);
      return res.data;
    },
    onSuccess: (_data, id) => invalidate(id),
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut marcar com a no llegida")),
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateThread();
  return useMutation<WaNoteItem, Error, { id: string; body: string }>({
    mutationKey: whatsappInboxKeys.threadAction(),
    mutationFn: async ({ id, body }) => {
      const res = await api.post<WaNoteItem>(`${BASE}/conversations/${id}/notes`, { body });
      return res.data;
    },
    onSuccess: (created, { id }) => {
      // La nota surt al moment; la invalidació ho confirma.
      queryClient.setQueryData<WaNoteItem[]>(whatsappInboxKeys.notes(id), (prev) =>
        prev && !prev.some((n) => n.id === created.id) ? [...prev, created] : prev,
      );
      return invalidate(id);
    },
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut desar la nota")),
  });
}

export function usePatchContact() {
  const invalidate = useInvalidateInbox();
  return useMutation<WaContactSummary, Error, { id: string; patch: WaPatchContact }>({
    mutationFn: async ({ id, patch }) => {
      const res = await api.patch<WaContactSummary>(`${BASE}/contacts/${id}`, patch);
      return res.data;
    },
    onSuccess: () => invalidate(),
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut actualitzar el contacte")),
  });
}

/** ADMIN: resol totes les converses amb activitat anterior a `before` (triatge del backfill). */
export function useResolveBefore() {
  const invalidate = useInvalidateInbox();
  return useMutation<{ resolved: number }, Error, { before: string }>({
    mutationFn: async ({ before }) => {
      const res = await api.post<{ resolved: number }>(`${BASE}/admin/resolve-before`, { before });
      return res.data;
    },
    onSuccess: (data) => {
      invalidate();
      toast.success(`${data.resolved} converses resoltes`);
    },
    onError: (error) => toast.error(errorMessage(error, "No s'han pogut resoldre les converses")),
  });
}

// ── Compositor i enllaços (F3) ──────────────────────────────────

/** Renderitza una resposta ràpida sobre la reserva de la conversa. No envia res. */
export function useRenderTemplate() {
  return useMutation<WaRenderTemplateResult, Error, { id: string; input: WaRenderTemplateInput }>({
    mutationFn: async ({ id, input }) => {
      const res = await api.post<WaRenderTemplateResult>(
        `${BASE}/conversations/${id}/render-template`,
        input,
      );
      return res.data;
    },
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut preparar la resposta")),
  });
}

/** «Marca com enviat»: apunta el text obert a WhatsApp (`PMS_WAME`); la conversa passa a Pendent. */
export function usePmsSend() {
  const invalidate = useInvalidateThread();
  return useMutation<WaPmsSendResult, Error, { id: string; text: string }>({
    mutationKey: whatsappInboxKeys.threadAction(),
    mutationFn: async ({ id, text }) => {
      const res = await api.post<WaPmsSendResult>(`${BASE}/conversations/${id}/pms-send`, { text });
      return res.data;
    },
    onSuccess: (_data, { id }) => invalidate(id),
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut marcar com enviat")),
  });
}

/** Enllaça la conversa amb una reserva i, si es demana, hi desa el telèfon del contacte. */
export function useLinkReservation() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateThread();
  return useMutation<WaLinkReservationResult, Error, { id: string; input: WaLinkReservationInput }>({
    mutationKey: whatsappInboxKeys.threadAction(),
    mutationFn: async ({ id, input }) => {
      const res = await api.post<WaLinkReservationResult>(
        `${BASE}/conversations/${id}/link-reservation`,
        input,
      );
      return res.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(whatsappInboxKeys.conversation(id), data.conversation);
      return invalidate(id);
    },
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut enllaçar la reserva")),
  });
}

// ── Miranda (F4, `plans/runbook-4-inbox-mati.md` §3.0) ─────────

/** Invalida el detall i les notes d'una conversa (i la llista, per l'estat i el punt de mode). */
function useInvalidateConversation() {
  const queryClient = useQueryClient();
  return (id: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: whatsappInboxKeys.conversation(id) }),
      queryClient.invalidateQueries({ queryKey: whatsappInboxKeys.notes(id) }),
      queryClient.invalidateQueries({ queryKey: [...whatsappInboxKeys.all, 'conversations'] }),
    ]);
}

/**
 * Les crides a Miranda poden trigar prop d'un minut: l'error ha de durar prou per veure'l i,
 * amb `id`, un segon clic substitueix el toast en lloc d'apilar-ne.
 */
const OBSERVE_TOAST_ID = 'wa-observe';
const OBSERVE_TOAST = { id: OBSERVE_TOAST_ID, duration: 10_000 };
const DRAFT_TOAST = { id: 'wa-draft', duration: 10_000 };

/** «Actualitza el resum»: l'observadora llegeix la conversa ara. `updated=false` → res no canvia. */
export function useObserveConversation() {
  const invalidate = useInvalidateConversation();
  return useMutation<WaObserveResult, Error, string>({
    mutationFn: async (id) => {
      const res = await api.post<WaObserveResult>(`${BASE}/conversations/${id}/observe`);
      return res.data;
    },
    onSuccess: (data, id) => {
      void invalidate(id);
      if (data.updated) toast.success('Resum actualitzat', { id: OBSERVE_TOAST_ID });
      else toast.error('Miranda no ha pogut llegir la conversa', OBSERVE_TOAST);
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Miranda no ha pogut llegir la conversa'), OBSERVE_TOAST),
  });
}

/** «Proposa una resposta»: crea un `AI_DRAFT` (409 si el mode no ho permet; el motiu va al toast). */
export function useRequestDraft() {
  const invalidate = useInvalidateConversation();
  return useMutation<WaNoteItem, Error, string>({
    mutationKey: whatsappInboxKeys.threadAction(),
    mutationFn: async (id) => {
      const res = await api.post<WaNoteItem>(`${BASE}/conversations/${id}/draft`);
      return res.data;
    },
    onSuccess: async (_data, id) => {
      toast.success('Esborrany de Miranda al fil', { id: DRAFT_TOAST.id });
      await invalidate(id);
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Miranda no ha pogut proposar cap resposta'), DRAFT_TOAST),
  });
}

/** «Fes-lo servir» d'un esborrany: marca `usedAt` (una sola vegada, al servidor). */
export function useMarkDraftUsed() {
  const invalidate = useInvalidateConversation();
  return useMutation<WaNoteItem, Error, { noteId: string; conversationId: string }>({
    mutationFn: async ({ noteId }) => {
      const res = await api.post<WaNoteItem>(`${BASE}/notes/${noteId}/used`);
      return res.data;
    },
    onSuccess: (_data, { conversationId }) => void invalidate(conversationId),
    onError: (error) =>
      toast.error(errorMessage(error, "No s'ha pogut marcar l'esborrany com a fet servir")),
  });
}

/** Configuració de l'inbox (`service_config.whatsapp_inbox`), llegida pel seu endpoint. */
export function useWhatsappInboxConfig(enabled = true) {
  return useQuery({
    queryKey: whatsappInboxKeys.config(),
    queryFn: async () => {
      const res = await api.get<WhatsappInboxConfig>(`${BASE}/config`);
      return res.data;
    },
    enabled,
    staleTime: 60_000,
  });
}

/** ADMIN: desa només les claus de `WaInboxConfigPatch` (mai `POST /service-config`). */
export function useUpdateWhatsappInboxConfig() {
  const queryClient = useQueryClient();
  return useMutation<WhatsappInboxConfig, Error, WaInboxConfigPatch>({
    mutationFn: async (patch) => {
      const res = await api.patch<WhatsappInboxConfig>(`${BASE}/config`, patch);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(whatsappInboxKeys.config(), data);
      // El mode per defecte canvia el mode efectiu de les converses que hereten.
      void queryClient.invalidateQueries({ queryKey: whatsappInboxKeys.all });
      toast.success("Configuració de l'inbox desada");
    },
    onError: (error) => toast.error(errorMessage(error, "No s'ha pogut desar la configuració")),
  });
}
